import { useState, useEffect, useCallback, useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import MainCard from "components/MainCard";

import { getRiwayatVerifikasi } from "./api";
import { getColumns } from "./columns";

import {
  Stack,
  TextField,
  InputAdornment,
  Box,
  Select,
  MenuItem,
  FormControl,
  Button
} from "@mui/material";

import { SearchOutlined, ReloadOutlined} from "@ant-design/icons";

import DialogDetail from "./DialogDetail";

export default function RiwayatVerifikasi() {

  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [pegawaiFilter, setPegawaiFilter] = useState("ALL");
  const [tanggalFilter, setTanggalFilter] = useState("");

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const fetchData = useCallback(async () => {

    try {

      setLoading(true);

      const data = await getRiwayatVerifikasi(token);

      const mapped = (data || [])
      .sort(
        (a, b) =>
          new Date(b.tanggal) -
          new Date(a.tanggal)
      )
      .map((item, index) => {

        const status = String(item.status ?? "")
          .toLowerCase()
          .trim();

        return {

          id: item.id,
          no: index + 1,

          tanggal: item.tanggal
            ? new Date(item.tanggal)
                .toLocaleDateString("id-ID")
            : "-",

          nip: item.nip ?? "-",
          nama_bawahan: item.nama_bawahan ?? "-",
          aktivitas: item.nama_kegiatan ?? "-",
          kategori: item.kategori ?? "-",
          lokasi: item.lokasi ?? "-",
          durasi: item.durasi ?? "-",
          status,

          catatan:
            status === "disetujui"
              ? item.catatan ?? "-"
              : status === "direvisi"
              ? item.catatan_revisi ?? "-"
              : status === "ditolak"
              ? item.catatan_penolakan ??
                item.catatan ??
                "-"
              : "-",

          deskripsi: item.deskripsi ?? "",
          link_bukti: item.link_bukti ?? ""

        };

      });

      setRows(mapped);

    } catch (err) {

      console.error("FETCH RIWAYAT ERROR:", err);

    } finally {

      setLoading(false);

    }

  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const pegawaiList = useMemo(() => {

    return [...new Set(rows.map((r) => r.nama_bawahan))];

  }, [rows]);

  const filteredRows = useMemo(() => {

    const q = search.toLowerCase();

    return rows.filter((row) => {

      const matchSearch =
        row.nama_bawahan.toLowerCase().includes(q) ||
        row.aktivitas.toLowerCase().includes(q) ||
        row.nip.toLowerCase().includes(q);

      const matchStatus =
        statusFilter === "ALL" ||
        row.status === statusFilter;

      const matchPegawai =
        pegawaiFilter === "ALL" ||
        row.nama_bawahan === pegawaiFilter;

      const matchTanggal =
        !tanggalFilter ||
        row.tanggal ===
          new Date(tanggalFilter).toLocaleDateString("id-ID");

      return (
        matchSearch &&
        matchStatus &&
        matchPegawai &&
        matchTanggal
      );

    });

  }, [rows, search, statusFilter, pegawaiFilter, tanggalFilter]);


  const handleResetFilter = () => {
    setSearch("");
    setStatusFilter("ALL");
    setPegawaiFilter("ALL");
    setTanggalFilter("");
  };

  const openDetail = (row) => {

    setSelectedRow(row);
    setDetailOpen(true);

  };

  const columns = getColumns(openDetail);

  return (

    <MainCard
      title="Riwayat Verifikasi Aktivitas"
      secondary={

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >

          {/* SEARCH */}
          <TextField
            size="small"
            placeholder="Cari aktivitas..."
            value={search}
            onChange={(e)=>setSearch(e.target.value)}
            sx={{
              minWidth: 230,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2
              }
            }}
            InputProps={{
              startAdornment:(
                <InputAdornment position="start">
                  <SearchOutlined />
                </InputAdornment>
              )
            }}
          />

          {/* STATUS */}
          <TextField
            select
            size="small"
            value={statusFilter}
            onChange={(e)=>setStatusFilter(e.target.value)}
            sx={{
              minWidth: 170,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2
              }
            }}
          >
            <MenuItem value="ALL">
              Semua Status
            </MenuItem>

            <MenuItem value="disetujui">
              Disetujui
            </MenuItem>

            <MenuItem value="direvisi">
              Direvisi
            </MenuItem>
          </TextField>

          {/* PEGAWAI */}
          <TextField
            select
            size="small"
            value={pegawaiFilter}
            onChange={(e)=>setPegawaiFilter(e.target.value)}
            sx={{
              minWidth: 180,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2
              }
            }}
          >
            <MenuItem value="ALL">
              Semua Pegawai
            </MenuItem>

            {pegawaiList.map((nama)=>(
              <MenuItem
                key={nama}
                value={nama}
              >
                {nama}
              </MenuItem>
            ))}
          </TextField>

          {/* TANGGAL */}
          <TextField
            type="date"
            size="small"
            value={tanggalFilter}
            onChange={(e)=>setTanggalFilter(e.target.value)}
            sx={{
              minWidth: 165,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2
              }
            }}
          />

          {/* RESET */}
          <Button
            variant="outlined"
            size="medium"
            startIcon={<ReloadOutlined />}
            onClick={handleResetFilter}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              px: 2
            }}
          >
            Reset Filter
          </Button>

        </Stack>

      }
    >

      <Box sx={{ height:540 }}>

        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[5,10,20,50]}
          initialState={{
            pagination:{
              paginationModel:{ pageSize:10 }
            }
          }}
        />

      </Box>

      <DialogDetail
        open={detailOpen}
        onClose={()=>setDetailOpen(false)}
        activity={selectedRow}
      />

    </MainCard>

  );

}